"use client";

import { HeaderEvents, VideoEvents, trackEvent } from "@punchup/analytics";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PlayCircle, UnlockKeyhole, Volume2, VolumeX } from "lucide-react";
import { useParams, usePathname } from "next/navigation";

import Link from "next/link";
import Player from "video.js/dist/types/player";
import { SpecialRow } from "@punchup/data/legacy/specials";
import { SubHeader2 } from "@punchup/ui-common/components/common";
import { VideoComedianContext } from "@punchup/data/legacy/videoPlayback";
import FeatureErrorBoundary from "@punchup/ui-common/components/common/FeatureErrorBoundary";
import VideoOverlaySuggestedSpecialTile from "./VideoOverlaySuggestedSpecialTile";
import { VideoState } from "../../types";
import VideoTicketLinkOverlay from "./VideoTicketLinkOverlay";
import { Statsig, useGateWithExposureLoggingDisabled } from "statsig-react";
import { genAbsoluteURL } from "@punchup/utils/genAbsoluteURL";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { useInterval } from "@punchup/utils/hooks";

// Sample comedic 'personal messages' to add variety each time.
const COMEDIAN_MESSAGES = [
  "But let's keep them laughs coming!",
  "Did you like it? Am I funny?",
  "Join my Guest List?  Everyone's doing it.",
  "Wanna keep watching?",
  "Don't pretend you have standards now.",
];

/**
 * A `SpecialRow` optionally tagged with its OWN comedian's display name. The
 * personalized autoplay endpoint sets this (recs can be cross-comedian); the
 * legacy same-comedian `recommendedVideos` leave it undefined and fall back to
 * the current page's comedian.
 */
type SuggestedSpecial = SpecialRow & {
  comedian_display_name?: string | null;
};

/** Where an "up next" pick originated, for analytics. */
type PickSource = "personalized" | "recommended_fallback";

/** A resolved end-screen pick plus the metadata selection analytics needs. */
type Pick = {
  special: SuggestedSpecial;
  source: PickSource;
  slot: "primary" | "secondary";
};

/**
 * A single "what plays next" choice on the end screen: poster on top, title and
 * comedian underneath. The whole card is the click target (plays that special).
 * The primary card gets the brand ring + auto-advance countdown badge.
 */
function NextUpCard({
  special,
  comedianName,
  isPrimary,
  countdown,
  onSelect,
}: {
  special: SpecialRow;
  comedianName?: string | null;
  isPrimary: boolean;
  countdown: number | null;
  onSelect: () => void;
}) {
  const posterSource = special.poster_img_with_titles ?? special.preview_img;
  const posterUrl = posterSource ? getCDNImageURL(posterSource) : null;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Play ${special.title ?? "next special"}`}
      className="group pointer-events-auto flex min-w-0 flex-1 basis-0 touch-manipulation flex-col text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 sm:max-w-[220px] lg:max-w-[300px]"
    >
      <div
        className={`relative aspect-video w-full overflow-hidden rounded-md ring-1 transition ${
          isPrimary
            ? "ring-2 ring-[#ff5e26]"
            : "ring-white/15 group-hover:ring-white/60"
        }`}
      >
        {posterUrl ? (
          <img
            src={posterUrl}
            alt={special.title ?? ""}
            className="h-full w-full object-cover transition duration-200 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-neutral-800 text-[10px] text-white/40">
            No preview
          </div>
        )}
        {/* Decorative hover affordance — must never intercept taps. */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition group-hover:opacity-100">
          <PlayCircle className="h-8 w-8 text-white sm:h-10 sm:w-10 lg:h-14 lg:w-14" />
        </div>
        {isPrimary && countdown !== null && countdown > 0 && (
          <span className="pointer-events-none absolute bottom-1 right-1 rounded-full bg-black/80 px-1.5 py-0.5 text-[10px] font-semibold text-white sm:text-xs lg:bottom-2 lg:right-2 lg:px-2 lg:py-1 lg:text-sm">
            Playing in {countdown}
          </span>
        )}
      </div>
      {/* Vertical gap between the poster and the title. */}
      <span className="mt-2 line-clamp-2 text-[11px] font-semibold leading-tight text-white sm:text-sm lg:mt-3 lg:text-lg">
        {special.title}
      </span>
      {comedianName ? (
        <span className="mt-0.5 truncate text-[10px] text-white/55 sm:text-xs lg:text-sm">
          {comedianName}
        </span>
      ) : null}
    </button>
  );
}

export default function VideoOverlays({
  context,
  videoState,
  onPlayNext,
  player,
  showEndOverlay = true, // necessary to maintain control modals (this param isn't passed by control)
  onCancel,
  explicitNextSpecial,
  secondaryNextSpecial,
  currentSpecialId,
  disableRecommendedFallback = false,
}: {
  context: VideoComedianContext & {
    isLoggedIn?: boolean;
    isFollowing?: boolean;
    specialName?: string;
    comedicMessages?: string[];
  };
  videoState: VideoState;
  onPlayNext: (special: SpecialRow) => void;
  player: Player | null;
  showEndOverlay?: boolean;
  onCancel?: () => void;
  explicitNextSpecial?: SuggestedSpecial | null;
  /**
   * Second "what plays next" pick. When present (alongside the primary), the
   * end screen shows two side-by-side tiles; the countdown still auto-plays the
   * primary, and selecting the alternate plays it instead.
   */
  secondaryNextSpecial?: SuggestedSpecial | null;
  /**
   * The currently-playing special. Never recommended as "up next" — guards the
   * legacy `recommendedVideos` fallback (a static, server-rendered list) from
   * suggesting the video you're already watching after an in-place autoplay.
   */
  currentSpecialId?: string | null;
  /**
   * Ignore the legacy `context.recommendedVideos` fallback entirely, so the
   * "up next" picks come ONLY from the explicit (personalized) specials. The
   * persistent player sets this: its autoplay navigates between specials, which
   * requires the routing metadata the personalized picks carry. Legacy recs
   * lack it, so auto-advancing to one swaps the source in place WITHOUT
   * navigating — leaving the page chrome on the previous special. When there's
   * no explicit pick, the player should simply stop rather than mis-advance.
   */
  disableRecommendedFallback?: boolean;
}) {
  const [endedTimeUntilNext, setEndedTimeUntilNext] = useState<number | null>(
    5
  );
  const [personalMessage, setPersonalMessage] = useState<string>("");

  // Gates the redesigned "pick your next" end screen (poster cards, optional
  // second tile). OFF falls back to the original single-pick overlay so the
  // visual rewrite can be rolled back with a flag flip rather than a revert.
  // The pick resolution + analytics below run in BOTH arms, so the rollout has
  // a consistent denominator to compare against.
  //
  // Exposure logging is DISABLED on the read: this component renders for every
  // viewer — including logged-out preview watchers who never reach the end
  // screen (the unlock overlay shows instead, see the eligibility guards
  // below). Auto-logging here exposed those userID-less users into the
  // experiment, polluting the population and tripping Statsig's "missing
  // userID" Pulse warning. Instead we manually log the exposure in the
  // impression effect, gated on the SAME eligibility condition that decides the
  // end screen is actually shown — so exposure fires only for users who can
  // truly receive the treatment.
  const { value: pickYourNextEndScreenEnabled } =
    useGateWithExposureLoggingDisabled("pick_your_next_end_screen");

  // Resolve the end-screen picks once per input change. These are read by
  // several effects' dependency arrays (auto-advance, impression, preload), so
  // memoizing keeps their references stable across the per-second countdown
  // re-renders — without it each tick produces fresh arrays and needlessly
  // re-runs those effects.
  const { nextVideo, secondVideo, nextSource, picks } = useMemo(() => {
    // Never suggest the special that's currently playing. The personalized
    // endpoint already excludes it, but the legacy `recommendedVideos` fallback
    // is a static server-rendered list that isn't re-filtered as you autoplay in
    // place — without this guard it can recommend the video back to itself.
    const isPlayable = (
      s: SuggestedSpecial | null | undefined
    ): s is SuggestedSpecial => Boolean(s) && s!.id !== currentSpecialId;

    const recommendedFallback = disableRecommendedFallback
      ? []
      : (context?.recommendedVideos ?? []).filter(isPlayable);

    const nextVideo =
      (isPlayable(explicitNextSpecial) ? explicitNextSpecial : null) ??
      recommendedFallback[0] ??
      null;

    // Optional second pick for the two-tile "pick your next" end screen. Falls
    // back to the next recommended video when no explicit secondary is provided.
    const secondVideo =
      (isPlayable(secondaryNextSpecial) ? secondaryNextSpecial : null) ??
      recommendedFallback.find((s) => s.id !== nextVideo?.id) ??
      null;
    // Only show two tiles when the redesign is on AND we have a distinct second
    // pick. Gating here (not just in render) keeps `picks.length` honest for the
    // analytics arms: the legacy overlay only ever shows the single primary.
    const hasTwoPicks =
      pickYourNextEndScreenEnabled &&
      Boolean(nextVideo && secondVideo && secondVideo.id !== nextVideo.id);

    // Where each pick came from, for analytics: a personalized rec from the
    // explicit endpoint vs the legacy static `recommendedVideos` fallback. These
    // mirror the nextVideo/secondVideo resolution above exactly.
    const nextSource: PickSource = isPlayable(explicitNextSpecial)
      ? "personalized"
      : "recommended_fallback";
    const secondSource: PickSource = isPlayable(secondaryNextSpecial)
      ? "personalized"
      : "recommended_fallback";

    // The picks shown on the end screen, primary first (auto-advances on
    // countdown). Each carries its source + slot so selection analytics can
    // distinguish primary-vs-alternate and personalized-vs-fallback.
    const picks: Pick[] = hasTwoPicks
      ? [
          { special: nextVideo!, source: nextSource, slot: "primary" },
          { special: secondVideo!, source: secondSource, slot: "secondary" },
        ]
      : nextVideo
        ? [{ special: nextVideo, source: nextSource, slot: "primary" }]
        : [];

    return { nextVideo, secondVideo, nextSource, picks };
  }, [
    explicitNextSpecial,
    secondaryNextSpecial,
    currentSpecialId,
    disableRecommendedFallback,
    context?.recommendedVideos,
    pickYourNextEndScreenEnabled,
  ]);

  const params = useParams();
  const pathName = usePathname();
  const absolutePath = genAbsoluteURL(
    `${params.comedianSlug}/follow?redirectRoute=${pathName}&comedianSlug=${params.comedianSlug}`
  );

  // If comedic messages exist in context, incorporate them.
  useEffect(() => {
    const messages = context.comedicMessages || COMEDIAN_MESSAGES;
    const randomMsg = messages[Math.floor(Math.random() * messages.length)];
    setPersonalMessage(randomMsg);
  }, [context.comedicMessages]);

  useInterval(
    () => {
      if (videoState.isEnded && endedTimeUntilNext !== null) {
        setEndedTimeUntilNext((prev) =>
          prev !== null && prev > 0 ? prev - 1 : null
        );
      }
    },
    videoState.isEnded ? 1000 : null
  );

  useEffect(() => {
    if (!videoState.isEnded) {
      setEndedTimeUntilNext(5);
    }
  }, [videoState.isEnded]);

  // Play a specific pick, cancelling the auto-advance countdown.
  const playSpecial = useCallback(
    (special: SpecialRow) => {
      setEndedTimeUntilNext(null);
      onPlayNext(special);
    },
    [onPlayNext]
  );

  const goToNextVideo = useCallback(() => {
    if (!nextVideo) {
      return;
    }
    playSpecial(nextVideo);
  }, [nextVideo, playSpecial]);

  useEffect(() => {
    if (endedTimeUntilNext === 0 && videoState.isEnded) {
      // Prevent auto-play if the Unlock overlay is showing:
      // (i.e. if we're in preview mode and the user is not logged in or not subscribed)
      if (context.isPreview && (!context.isLoggedIn || !context.isFollowing)) {
        return;
      }
      trackEvent(VideoEvents.PLAY_NEXT_VIDEO_AUTO, {
        from_special_id: currentSpecialId ?? null,
        special_id: nextVideo?.id ?? null,
        slot: "primary",
        source: nextSource,
        auto: true,
        pick_count: picks.length,
      });
      goToNextVideo();
    }
    // currentSpecialId/nextVideo/nextSource/picks are read by the trackEvent
    // above and MUST be in deps so the auto-advance logs the special that just
    // finished — not a stale closure from when the effect was first registered
    // (e.g. the prior special after an in-place autoplay). Re-running on these
    // is safe: the `endedTimeUntilNext === 0` gate plus the `null` reset in
    // playSpecial prevents a second fire/advance.
  }, [
    videoState.isEnded,
    endedTimeUntilNext,
    goToNextVideo,
    context,
    currentSpecialId,
    nextVideo,
    nextSource,
    picks,
  ]);

  // Fire one impression per end-screen appearance so selection events have a
  // denominator (selection rate / CTR) and we can compare 1-pick vs 2-pick
  // screens. The ref guards against re-firing when picks re-resolve mid-show;
  // it resets when the video is no longer ended (next cycle re-impresses).
  const overlayImpressedRef = useRef(false);
  useEffect(() => {
    if (!videoState.isEnded) {
      overlayImpressedRef.current = false;
      return;
    }
    // The preview unlock overlay shows instead of the picks — don't count it.
    if (context.isPreview && (!context.isLoggedIn || !context.isFollowing)) {
      return;
    }
    if (overlayImpressedRef.current || picks.length === 0) {
      return;
    }
    overlayImpressedRef.current = true;
    // Log the gate exposure HERE rather than at the useGate read: this is the
    // first moment an eligible user actually sees the end screen, so the
    // exposure point matches the decision point and every exposure carries a
    // valid userID. Fires once per appearance via the same ref guard above.
    Statsig.manuallyLogGateExposure("pick_your_next_end_screen");
    trackEvent(VideoEvents.SHOWED_PLAY_NEXT_OVERLAY, {
      from_special_id: currentSpecialId ?? null,
      pick_count: picks.length,
      primary_special_id: picks[0]?.special.id ?? null,
      primary_source: picks[0]?.source ?? null,
      secondary_special_id: picks[1]?.special.id ?? null,
      secondary_source: picks[1]?.source ?? null,
    });
  }, [videoState.isEnded, picks, context, currentSpecialId]);

  // Preload the posters of the picks we'll show, so the end screen renders with
  // images already decoded. Keyed on the poster paths so it only re-runs when a
  // pick actually changes.
  const nextPoster =
    nextVideo?.poster_img_with_titles ?? nextVideo?.preview_img ?? null;
  const secondPoster =
    secondVideo?.poster_img_with_titles ?? secondVideo?.preview_img ?? null;
  useEffect(() => {
    if (typeof window === "undefined") return;
    [nextPoster, secondPoster].forEach((path) => {
      if (path) new window.Image().src = getCDNImageURL(path);
    });
  }, [nextPoster, secondPoster]);

  // PREVIEW MODE OVERLAY
  if (context.isPreview) {
    return (
      <div id="vjs-punchup-overlays" className="relative">
        <>
          {/* Mute/Unmute button */}
          <button
            className="absolute top-3 right-4 vjs-mute-button"
            onClick={() => {
              player?.muted(!player?.muted());
            }}
            aria-label="Toggle audio"
          >
            {player?.muted() ? <VolumeX size={28} /> : <Volume2 size={28} />}
          </button>

          {/* Preview Badge */}
          <div className="absolute top-0 left-0 p-3 rounded-br vjs-overlay">
            <p className="text-[15px] font-semibold tracking-wide">PREVIEW</p>
          </div>

          {/* Subscribe overlay if ended */}
          {showEndOverlay &&
            videoState.isEnded &&
            (!context.isLoggedIn || !context.isFollowing) && (
              // Updated container to center the overlay exactly in the player on mobile
              <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-auto">
                <div className="bg-gray-900 bg-opacity-80 p-4 sm:p-6 rounded-lg shadow-2xl max-w-xs w-11/12 sm:w-full mx-auto text-center">
                  {/* Show image only on desktop */}
                  {context.comedian.about_img && (
                    <img
                      src={getCDNImageURL(context.comedian.about_img)}
                      alt={context.comedian.display_name}
                      className="hidden sm:block rounded-full w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-4"
                    />
                  )}
                  <div className="mb-3 sm:mb-4">
                    <p
                      style={{
                        textAlign: "center",
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontFamily: "Inter",
                      }}
                      className="text-white text-[16px] sm:text-[18px] font-bold leading-[22px]"
                    >
                      This is just a preview!
                    </p>
                    {/* Show personal message only on desktop */}
                    <p
                      style={{
                        textAlign: "center",
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontFamily: "Inter",
                      }}
                      className="hidden sm:block text-white text-[10px] sm:text-[12px] font-medium leading-[18px] mt-1"
                    >
                      {personalMessage}
                    </p>
                    <p
                      style={{
                        textAlign: "center",
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontFamily: "Inter",
                      }}
                      className="text-white text-[10px] sm:text-[12px] font-medium leading-[18px] mt-2"
                    >
                      Subscribe to watch the full thing.
                    </p>
                  </div>
                  <Link
                    className="flex items-center gap-2 justify-center bg-[#ff5e26] hover:bg-orange-600 px-3 sm:px-4 py-2 rounded focus:outline-none"
                    onClick={() => {
                      trackEvent(HeaderEvents.CLICKED_SUBSCRIBE_FROM_HEADER);
                    }}
                    href={absolutePath}
                  >
                    <UnlockKeyhole size={20} color="#ffffff" />
                    <span className="text-xs sm:text-sm font-medium text-white">
                      Subscribe to Continue
                    </span>
                  </Link>
                </div>
              </div>
            )}
        </>
      </div>
    );
  }

  // MAIN OVERLAY (not preview)
  // One or two picks, side by side. Primary first (auto-advances on countdown).
  return (
    <div id="vjs-punchup-overlays">
      {showEndOverlay &&
        videoState.isEnded &&
        picks.length > 0 &&
        (pickYourNextEndScreenEnabled ? (
          <div className="pointer-events-auto absolute inset-0 z-50 flex flex-col items-center justify-center gap-2 overflow-y-auto bg-black/80 px-3 py-3 backdrop-blur-md sm:gap-5 sm:px-4 sm:py-6 lg:gap-7">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70 sm:text-sm lg:text-base">
              Up Next
            </p>
            <div className="flex w-full max-w-md flex-row items-start justify-center gap-2.5 sm:max-w-xl sm:gap-5 lg:max-w-3xl lg:gap-7">
              {picks.map((pick, i) => (
                <NextUpCard
                  key={pick.special.id}
                  special={pick.special}
                  comedianName={
                    pick.special.comedian_display_name ??
                    context.comedian?.display_name
                  }
                  isPrimary={i === 0}
                  countdown={i === 0 ? endedTimeUntilNext : null}
                  onSelect={() => {
                    playSpecial(pick.special);
                    trackEvent(VideoEvents.PLAY_NEXT_VIDEO, {
                      from_special_id: currentSpecialId ?? null,
                      special_id: pick.special.id,
                      slot: pick.slot,
                      source: pick.source,
                      auto: false,
                      pick_count: picks.length,
                    });
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              className="pointer-events-auto rounded-full px-4 py-1.5 text-[11px] font-medium text-white/70 transition hover:bg-white/10 hover:text-white sm:text-sm lg:px-5 lg:py-2 lg:text-base"
              onClick={() => {
                onCancel?.();
                setEndedTimeUntilNext(null);
                trackEvent(VideoEvents.CANCEL_PLAY_NEXT_VIDEO, {
                  from_special_id: currentSpecialId ?? null,
                  pick_count: picks.length,
                });
              }}
            >
              Dismiss
            </button>
          </div>
        ) : (
          // Legacy single-pick overlay (gate OFF). Preserved verbatim so the
          // redesign can be dialed back to known-good behavior with a flag flip.
          nextVideo && (
            <div
              id="vjs-center-ended-overlay"
              className="flex flex-col items-center justify-center p-6 rounded vjs-overlay"
            >
              <div className="w-full text-sm md:text-base font-semibold mb-2">
                Up Next
              </div>
              <div className="vjs-ended-container">
                <div className="mb-3">
                  <VideoOverlaySuggestedSpecialTile
                    special={nextVideo}
                    context={context}
                  />
                  <SubHeader2>{nextVideo.title}</SubHeader2>
                </div>
                <div>
                  <button
                    className="flex items-center w-full gap-2 mb-2 transition-all duration-100 ease-in-out shadow-sm btn justify-stretch primary"
                    onClick={() => {
                      goToNextVideo();
                      trackEvent(VideoEvents.PLAY_NEXT_VIDEO);
                    }}
                  >
                    {endedTimeUntilNext ? (
                      <>Play Next ({endedTimeUntilNext})</>
                    ) : (
                      "Playing Next"
                    )}
                  </button>
                  <button
                    className="flex items-center w-full gap-2 transition-all duration-100 ease-in-out shadow-sm btn justify-stretch"
                    onClick={() => {
                      onCancel?.();
                      setEndedTimeUntilNext(null);
                      trackEvent(VideoEvents.CANCEL_PLAY_NEXT_VIDEO);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )
        ))}
      <FeatureErrorBoundary name="VideoTicketLinkOverlay">
        <VideoTicketLinkOverlay context={context} videoState={videoState} />
      </FeatureErrorBoundary>
    </div>
  );
}
